/**
 * Tomoe Gozen — „Unmatched: Sun's Origin".
 *
 * ATTACK OF OPPORTUNITY: keď súperov hrdina opustí zónu Tomoe, dostane
 * 1 poškodenie. Rieši to `onFighterMoved` (hák `movedHooks` v ops.ts) pri
 * presune, položení aj výmene miest.
 */

import { areAdjacent, neighbors, shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, dealDamage, drawCard, emit, endTurnNow, gainAction, getMap, getPlayer, healFighter, returnFromDiscard } from '../ops.js';
import { areEnemies } from '../players.js';
import type { FighterId, PlayerIndex, SpaceId } from '../types.js';
import { draw, skirmish } from './common.js';
import { askMove, fighterOrNull, opponentDiscards, opponentOf, revealHand } from './helpers.js';
import { cappedAtTwo } from './spiderMan.js';
import type { CardEffect, EffectRegistry, HeroSpecial } from './types.js';

const TOMOE = 'tomoe-gozen';
const FIVE = 'tomoe-gozen.five-against-thousands';
const WAY = 'tomoe-gozen.a-warriors-way';
const KISO = 'tomoe-gozen.lord-kisos-final-stand';

const tomoeOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === TOMOE) ?? null;

/** Susedí figúrka s Tomoe? */
function nextToTomoe(ctx: Ctx, player: PlayerIndex, id: FighterId | null): boolean {
  const tomoe = tomoeOf(ctx, player);
  const other = fighterOrNull(ctx, id);
  return Boolean(tomoe && other && areAdjacent(getMap(ctx), tomoe.spaceId, other.spaceId));
}

function adjacentToTomoe(ctx: Ctx, player: PlayerIndex): FighterId[] {
  const tomoe = tomoeOf(ctx, player);
  if (!tomoe) return [];
  const around = new Set(neighbors(getMap(ctx), tomoe.spaceId));
  return ctx.state.fighters.filter((f) => around.has(f.spaceId)).map((f) => f.id);
}

const isHero = (ctx: Ctx, id: FighterId | null) => fighterOrNull(ctx, id)?.kind === 'hero';

/**
 * „IMMEDIATELY: Tomoe Gozen may swap spaces with an adjacent fighter. AFTER:
 * Deal 1 damage to an adjacent fighter." Výmena nemení úlohy v súboji.
 */
const fearsomeStrength: CardEffect = {
  immediate: (e) => {
    const tomoe = tomoeOf(e.ctx, e.player);
    const candidates = adjacentToTomoe(e.ctx, e.player);
    if (!tomoe || candidates.length === 0) return;
    askChoice(e.ctx, {
      kind: 'swap',
      player: e.player,
      source: e.cardId,
      label: 'Fearsome Strength: smieš si vymeniť miesto so susednou figúrkou',
      optional: true,
      fighterId: tomoe.id,
      candidates,
      keepRoles: true,
    });
  },
  after: (e) => {
    const targets = adjacentToTomoe(e.ctx, e.player);
    if (targets.length === 0) return;
    askChoice(e.ctx, {
      kind: 'damage',
      player: e.player,
      source: e.cardId,
      label: 'Fearsome Strength: 1 poškodenie susednej figúrke',
      optional: false,
      amount: 1,
      targets,
    });
  },
};

/** „This attack can only target a fighter adjacent to Tomoe Gozen. Play it face up. AFTER: End the turn." */
const witnessMyLastBattle: CardEffect = {
  adjacentOnly: true,
  playFaceUp: true,
  after: (e) => endTurnNow(e.ctx),
};

/** „If Tomoe Gozen is adjacent to the opposing fighter, you may discard a card to put this card back into your hand." */
const fiveAgainstThousands: CardEffect = {
  after: (e) => {
    if (!nextToTomoe(e.ctx, e.player, e.opponentFighterId) || getPlayer(e.ctx.state, e.player).hand.length === 0) return;
    askChoice(e.ctx, {
      kind: 'confirm',
      player: e.player,
      source: e.cardId,
      label: 'Five Against Thousands: odhodiť kartu a vziať si túto späť do ruky?',
      optional: true,
      effect: FIVE,
      yes: 'Odhodiť a vziať späť',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, choice) => {
    askChoice(ctx, {
      kind: 'card',
      player: choice.player,
      source: choice.source,
      label: 'Five Against Thousands: odhoď kartu',
      optional: false,
      from: 'hand',
      purpose: 'discard',
      discards: true,
    });
    // Karta zo súboja je v odhode až po upratovaní — vráti sa, keď príde na rad.
    askChoice(ctx, {
      kind: 'auto',
      player: choice.player,
      source: choice.source,
      label: 'Five Against Thousands: karta späť do ruky',
      optional: false,
      follow: { kind: 'effect', effect: FIVE, data: choice.source ?? '' },
    });
  },
  onFollow: (ctx, choice, cardId) => {
    if (getPlayer(ctx.state, choice.player).discard.includes(cardId)) returnFromDiscard(ctx, choice.player, [cardId]);
  },
};

/** „Move the opposing fighter up to 3 spaces. Then, if they are a hero, place Tomoe Gozen in a space adjacent to them." */
const aWarriorsWay: CardEffect = {
  after: (e) => {
    const them = fighterOrNull(e.ctx, e.opponentFighterId);
    if (!them) return;
    askMove(e, { fighters: [them.id], max: 3, label: "A Warrior's Way: presuň súperovu figúrku až o 3" });
    if (them.kind !== 'hero') return;
    askChoice(e.ctx, {
      kind: 'auto',
      player: e.player,
      source: e.cardId,
      label: "A Warrior's Way: Tomoe k súperovi",
      optional: false,
      follow: { kind: 'effect', effect: WAY, data: them.id },
    });
  },
  onFollow: (ctx, choice, heroId) => {
    const tomoe = tomoeOf(ctx, choice.player);
    const hero = fighterOrNull(ctx, heroId);
    if (!tomoe || !hero) return;
    const occupied = new Set(ctx.state.fighters.map((f) => f.spaceId));
    const spaces = neighbors(getMap(ctx), hero.spaceId).filter((id) => !occupied.has(id));
    if (spaces.length === 0) return;
    askChoice(ctx, {
      kind: 'place',
      player: choice.player,
      source: choice.source,
      label: "A Warrior's Way: polož Tomoe vedľa súperovho hrdinu",
      optional: false,
      fighterId: tomoe.id,
      spaces,
    });
  },
};

/** „Draw 1 card. Then, if you lost the combat, look at your opponent's hand and choose 1 card for them to discard." */
const confrontAnyDemon: CardEffect = {
  after: (e) => {
    drawCard(e.ctx, e.player);
    const opponent = opponentOf(e);
    if (e.won || opponent === null || getPlayer(e.ctx.state, opponent).hand.length === 0) return;
    if (!revealHand(e, opponent)) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Confront Any Demon or God: vyber kartu, ktorú súper odhodí',
      optional: false,
      from: 'hand',
      owner: opponent,
      purpose: 'discard',
    });
  },
};

/** „If the opposing fighter is a hero, this card's value is 5 instead." */
const aWorthyOpponent: CardEffect = {
  during: (e, value) => (isHero(e.ctx, e.opponentFighterId) ? 5 : value),
};

/**
 * „IMMEDIATELY: If the opposing fighter is a hero, cancel all effects on your
 * opponent's card. AFTER: If the opposing fighter is adjacent, deal 1 damage to them."
 */
const flashOfSteel: CardEffect = {
  cancelsOpponentIf: (e) => isHero(e.ctx, e.opponentFighterId),
  after: (e) => {
    if (e.opponentFighterId && nextToTomoe(e.ctx, e.player, e.opponentFighterId)) dealDamage(e.ctx, e.opponentFighterId, 1);
  },
};

/**
 * „IMMEDIATELY: Tomoe Gozen cannot leave her space for the rest of the turn.
 * AFTER: If the opposing fighter is adjacent, your opponent discards 1 card."
 */
const refuseToRetreat: CardEffect = {
  immediate: (e) => {
    const tomoe = tomoeOf(e.ctx, e.player);
    if (!tomoe) return;
    (e.ctx.state.turn.pinned ??= []).push(tomoe.id);
    emit(e.ctx, { type: 'specialUsed', player: e.player, heroId: TOMOE, name: 'Refuse to Retreat: Tomoe tento ťah neopustí svoj priestor' });
  },
  after: (e) => {
    if (nextToTomoe(e.ctx, e.player, e.opponentFighterId)) opponentDiscards(e, opponentOf(e));
  },
};

/**
 * „Move Tomoe Gozen up to 3 spaces. She may move through sidekicks. Then,
 * choose one: Tomoe Gozen recovers 2 health / gain 1 action."
 */
const lordKisosFinalStand: CardEffect = {
  scheme: (e) => {
    const tomoe = tomoeOf(e.ctx, e.player);
    if (tomoe) askMove(e, { fighters: [tomoe.id], max: 3, through: 'sidekicks', label: "Lord Kiso's Final Stand: presuň Tomoe až o 3 (smie cez sidekickov)" });
    const follow = (data: string) => ({ kind: 'effect' as const, effect: KISO, data });
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: "Lord Kiso's Final Stand: vyber jedno",
      optional: false,
      options: [
        { label: 'Tomoe si vylieči 2', follow: follow('heal') },
        { label: 'Získaj 1 akciu', follow: follow('action') },
      ],
    });
  },
  onFollow: (ctx, choice, data) => {
    if (data === 'action') {
      gainAction(ctx, choice.player);
      return;
    }
    const tomoe = tomoeOf(ctx, choice.player);
    if (tomoe) healFighter(ctx, tomoe.id, 2);
  },
};

export const tomoeCards: EffectRegistry = {
  'tomoe-gozen.fearsome-strength': fearsomeStrength,
  'tomoe-gozen.witness-my-last-battle': witnessMyLastBattle,
  [FIVE]: fiveAgainstThousands,
  [WAY]: aWarriorsWay,
  'tomoe-gozen.piercing-shot': draw(2),
  'tomoe-gozen.confront-any-demon-or-god': confrontAnyDemon,
  'tomoe-gozen.a-worthy-opponent': aWorthyOpponent,
  'tomoe-gozen.flash-of-steel': flashOfSteel,
  'tomoe-gozen.skirmish': skirmish(2),
  'tomoe-gozen.refuse-to-retreat': refuseToRetreat,
  'tomoe-gozen.deeds-of-valor': cappedAtTwo,
  [KISO]: lordKisosFinalStand,
};

export const tomoeSpecial: HeroSpecial = {
  name: 'Attack of Opportunity',
  /** „When an opposing hero leaves Tomoe Gozen's zone, deal 1 damage to that hero." */
  onFighterMoved: (ctx, player, fighter, path) => {
    const tomoe = tomoeOf(ctx, player);
    if (!tomoe || fighter.kind !== 'hero' || !areEnemies(ctx.state, fighter.owner, player)) return;
    const map = getMap(ctx);
    // `shareZone` pre ten istý priestor vracia false — Tomoe sama je v svojej zóne.
    const inZone = (space: SpaceId) => space === tomoe.spaceId || shareZone(map, space, tomoe.spaceId);
    if (!inZone(path[0]!) || path.slice(1).every(inZone)) return;
    emit(ctx, { type: 'specialUsed', player, heroId: TOMOE, name: `Attack of Opportunity: ${fighter.name} opúšťa zónu Tomoe` });
    dealDamage(ctx, fighter.id, 1);
  },
};

